import { supabase } from "@/integrations/supabase/client";

export interface WorkspaceMember {
  id: string;
  user_id: string | null;
  role: string;
  seat_status: string;
  created_at: string;
  profiles?: {
    name: string | null;
    email: string;
    avatar_url: string | null;
  } | null;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
}

export async function getCurrentWorkspace(userId: string) {
  const { data: memberships, error: membershipError } = await supabase
    .from('workspace_members')
    .select('workspace_id, workspaces(*)')
    .eq('user_id', userId)
    .eq('seat_status', 'active')
    .limit(1);

  if (membershipError) throw membershipError;
  if (!memberships || memberships.length === 0) return null;

  return memberships[0].workspaces;
}

export async function getUserWorkspaces(userId: string) {
  const { data: memberships, error } = await supabase
    .from('workspace_members')
    .select('workspace_id, workspaces(*)')
    .eq('user_id', userId)
    .eq('seat_status', 'active')
    .order('created_at', { ascending: false });

  if (error) throw error;
  
  return memberships?.map(m => m.workspaces).filter(Boolean) ?? [];
}

export async function getWorkspaceMembers(workspaceId: string) {
  const { data, error } = await supabase
    .from('workspace_members')
    .select(`
      id,
      user_id,
      role,
      seat_status,
      created_at,
      profiles (
        name,
        email,
        avatar_url
      )
    `)
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as WorkspaceMember[];
}

export async function getPendingInvites(workspaceId: string) {
  const { data, error } = await supabase
    .from('workspace_invites')
    .select('id, email, role, created_at, expires_at')
    .eq('workspace_id', workspaceId)
    .is('accepted_at', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as PendingInvite[];
}

export async function updateMemberRole(memberId: string, newRole: string) {
  const { error } = await supabase
    .from('workspace_members')
    .update({ role: newRole })
    .eq('id', memberId);

  if (error) throw error;
}

export async function removeMember(memberId: string) {
  const { error } = await supabase
    .from('workspace_members')
    .update({ seat_status: 'deactivated' })
    .eq('id', memberId);

  if (error) throw error;
}

export async function cancelInvite(inviteId: string) {
  const { error } = await supabase
    .from('workspace_invites')
    .delete()
    .eq('id', inviteId);

  if (error) throw error;
}

interface UpdateWorkspaceSettingsParams {
  workspaceId: string;
  primaryColor?: string;
  industry?: string;
  description?: string;
  logoUrl?: string;
}

export async function updateWorkspaceSettings({
  workspaceId,
  primaryColor,
  industry,
  description,
  logoUrl,
}: UpdateWorkspaceSettingsParams) {
  const updates: any = {};

  if (industry !== undefined) updates.industry = industry;
  if (description !== undefined) updates.description = description;

  // Update branding_settings JSON
  if (primaryColor !== undefined || logoUrl !== undefined) {
    const { data: currentWorkspace } = await supabase
      .from('workspaces')
      .select('branding_settings')
      .eq('id', workspaceId)
      .single();

    const currentBranding = (currentWorkspace?.branding_settings as Record<string, any>) || {};
    const brandingUpdates: Record<string, any> = { ...currentBranding };
    
    if (primaryColor) brandingUpdates.primary_color = primaryColor;
    if (logoUrl) brandingUpdates.logo_url = logoUrl;
    
    updates.branding_settings = brandingUpdates;
  }

  const { data, error } = await supabase
    .from('workspaces')
    .update(updates)
    .eq('id', workspaceId)
    .select()
    .single();

  if (error) throw error;
  return data;
}
